import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useCreateCouponMutation } from "Api/Mutations/Coupons/useCreateCouponMutation";
import {
  type CreateCouponFormData,
  getCreateCouponSchema,
} from "Components/Admin/Coupons/createCouponSchema";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { useRegionList } from "Hooks/Options/useRegionList";
import { CouponDiscountType, CouponType } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { Resources, useResource } from "Translations/Resources";
import { getCurrencySymbol, getRegionCurrency } from "Utils/CurrencyUtils";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.Coupons;

const DEFAULT_VALUES: Partial<CreateCouponFormData> = {
  isAutoGenerate: true,
  code: "",
  discountType: CouponDiscountType.Percentage,
  region: undefined, // No region preselect — the admin must consciously pick the coupon's market.
  expirationDate: "",
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateCouponDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useResource();
  const { codeListRegions } = useRegionList();

  const { control, handleSubmit, watch, reset } = useForm<CreateCouponFormData>(
    {
      resolver: zodResolver(getCreateCouponSchema(t)),
      defaultValues: DEFAULT_VALUES,
    },
  );

  const isAutoGenerate = watch("isAutoGenerate");
  const discountType = watch("discountType");
  const region = watch("region");
  const discountAdornment =
    discountType === CouponDiscountType.Percentage
      ? "%"
      : region
        ? getCurrencySymbol(getRegionCurrency(region))
        : "";

  const discountTypeOptions: Array<CodeListItem> = [
    { code: CouponDiscountType.Percentage, name: t(RESOURCES.Form.Percentage) },
    {
      code: CouponDiscountType.FixedAmount,
      name: t(RESOURCES.Form.FixedAmount),
    },
  ];

  const { mutate: createCoupon, isPending } = useCreateCouponMutation({
    onSuccess: (result) => {
      toast.success(`${t(RESOURCES.Messages.CreateSuccess)} — ${result.code}`);
      handleClose();
    },
    onError: () => {
      toast.error(t(RESOURCES.Messages.CreateError));
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = ({
    isAutoGenerate,
    code,
    discount,
    discountType,
    region,
    expirationDate,
    initialStock,
  }: CreateCouponFormData) =>
    createCoupon({
      code: isAutoGenerate ? null : (code?.trim().toUpperCase() ?? null),
      discount,
      discountType,
      region,
      expirationDate: new Date(expirationDate).toISOString(),
      type: CouponType.HunterLicense,
      initialStock,
    });

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t(RESOURCES.Form.DialogTitle)}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <CheckboxInput
              control={control}
              name="isAutoGenerate"
              label={t(RESOURCES.Form.AutoGenerate)}
            />
            {!isAutoGenerate && (
              <FormInput
                control={control}
                name="code"
                label={t(RESOURCES.Form.Code)}
                maxLength={50}
                valueTransformer={(value) => value.toUpperCase()}
              />
            )}
            <Dropdown
              control={control}
              name="region"
              label={t(RESOURCES.Form.Region)}
              codeList={codeListRegions}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Box flex={1}>
                <FormInput
                  control={control}
                  name="discount"
                  label={t(RESOURCES.Form.Discount)}
                  type="number"
                  unit={discountAdornment}
                />
              </Box>
              <Box flex={1}>
                <Dropdown
                  control={control}
                  name="discountType"
                  label={t(RESOURCES.Form.DiscountType)}
                  codeList={discountTypeOptions}
                />
              </Box>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Box flex={1}>
                <DateFormInput
                  control={control}
                  name="expirationDate"
                  label={t(RESOURCES.Form.Expiration)}
                />
              </Box>
              <Box flex={1}>
                <FormInput
                  control={control}
                  name="initialStock"
                  label={t(RESOURCES.Form.Stock)}
                  type="number"
                  min={1}
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t(RESOURCES.Form.Cancel)}</Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {t(RESOURCES.Form.Submit)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
